import { GetCurrentMember } from 'iam/SessionStore'
import { PrimeUser } from 'iam/UserStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import once from 'lodash/once'
import { computed, makeObservable, runInAction } from 'mobx'
import {
  GetOpportunityRequest,
  ListOpportunitiesRequest,
  Opportunity as OpportunityProto,
  ListQualifiedProvidersRequest,
  ApplyToOpportunityRequest,
  GetCandidaciesRequest,
  WithdrawApplicationRequest,
} from '@taxfyle/api-internal/internal/opportunity_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { merge, NEVER, Subject, concatMap, take } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { share, switchMap } from 'rxjs/operators'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import { filterNonNil, shareLatest } from 'utils/rx-util'
import { OpportunitiesAPI } from './API'
import Opportunity from './Opportunity'
import { OpportunityEvent } from './opportunity-events'
import TypeCase = RealtimeEvent.TypeCase
import { StatusCode } from 'grpc-web'
import { NotFound } from 'fejl'
import Member from '../iam/models/Member'

export const enum PickupResult {
  Success,
  Vanished,
  MaxConcurrentJobs,
}

/**
 * Opportunity store.
 */
export default class OpportunityStore {
  /**
   * Events synthesized locally. Used to immediately emit an `OpportunityRemoved` event
   * after picking up the job. If the real event comes in later, the mapping handler
   * will ignore it because the Op has already been removed from the local collection.
   *
   * @private
   */
  private readonly synthesizedEvents = new Subject<OpportunityEvent>()

  /**
   * Opportunities collection.
   */
  opportunities = collection<OpportunityProto.AsObject, Opportunity>({
    create: () =>
      new Opportunity(
        this.primeUser,
        this.primeWorkspace,
        this.getCurrentMember,
      ),
  })

  /**
   * Observable that, when first subscribed to, probes whether we can view opportunities.
   */
  readonly canViewOpportunities$ = this.currentMember$
    .pipe(
      take(1),
      switchMap(() => this.probeCanViewOpportunities()),
    )
    .pipe(shareLatest())

  /**
   * Opportunity events.
   */
  readonly events$: Observable<OpportunityEvent> = merge(
    this.synthesizedEvents,
    this.canViewOpportunities$.pipe(
      // Only connect to the realtime stream if we are allowed to view ops.
      switchMap((allowed) => (allowed ? merge(this.opsAPI.events$) : NEVER)),
      concatMap((e) => this.mapOpportunityRealtimeEvent(e)),
      filterNonNil(),
    ),
  ).pipe(share())

  /**
   * Constructor.
   */
  constructor(
    private opsAPI: OpportunitiesAPI,
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
    private getCurrentMember: GetCurrentMember,
    private currentMember$: Observable<Member>,
    private pickupDelegate: (opportunityId: Id) => Promise<PickupResult>,
  ) {
    makeObservable(this)

    this.events$.subscribe()
  }

  /**
   * List qualified providers.
   *
   * @param id
   * @param pageSize
   * @param pageToken
   * @returns
   */
  listQualifiedProviders(id: string, pageSize: number, pageToken: string) {
    const request = new ListQualifiedProvidersRequest()
      .setId(Id.assert(id))
      .setPageSize(pageSize)
      .setPageToken(pageToken)

    return this.opsAPI.listQualifiedProviders(request)
  }

  /**
   * The opportunity count. We know that any opportunity in the local collection
   * are available for pickup for the current user.
   */
  @computed
  get opportunityCount() {
    return this.opportunities.length
  }

  /**
   * Lists opportunities.
   *
   * @param query
   */
  async listOpportunities(
    query: ListOpportunitiesQuery,
  ): Promise<CursorPaged<Opportunity>> {
    const req = new ListOpportunitiesRequest()
    if (query.limit) {
      req.setPageSize(query.limit)
    }
    if (query.orderBy) {
      req.setOrderBy(createOrderByField(query.orderBy))
    }
    if (query.after) {
      req.setPageToken(query.after)
    }
    if (query.workspaceId) {
      req.setWorkspaceId(query.workspaceId)
    }
    if (query.searchText) {
      req.setSearchText(query.searchText)
    }

    const result = await this.opsAPI.listOpportunities(req).then((res) => ({
      data: this.opportunities.set(
        res.getOpportunitiesList().map((op) => op.toObject()),
      ),
      cursor: res.getNextPageToken(),
    }))

    await this.fetchCandidacies(result.data.map((x) => Id.assert(x.id))).then(
      (candidacies) =>
        candidacies.forEach((c) =>
          this.opportunities.get(c)?.markUserAsCandidate(true),
        ),
    )

    return result
  }

  /**
   * Fetches candidacy information for the given page of Opportunity IDs.
   */
  async fetchCandidacies(ids: string[]) {
    return this.opsAPI
      .getCandidacies(new GetCandidaciesRequest().setIdsList(ids))
      .then((res) => res.getCandidaciesList().map((x) => x.getId()))
  }

  /**
   * Get opportunity.
   *
   * @param id
   */
  async getOpportunity(id: string): Promise<Opportunity> {
    const result = await this._fetchOpportunity(id)
    const opportunity = new Opportunity(
      this.primeUser,
      this.primeWorkspace,
      this.getCurrentMember,
    )

    opportunity.set(opportunity.parse(result.toObject()))

    return opportunity
  }

  /**
   * Fetches an opportunity as a proto.
   *
   * @param id
   */
  async _fetchOpportunity(id: string): Promise<OpportunityProto> {
    try {
      const req = new GetOpportunityRequest()
      req.setId(id)

      return await this.opsAPI.getOpportunity(req)
    } catch (error: any) {
      if (error?.code === StatusCode.NOT_FOUND) {
        throw new NotFound(error?.message)
      }

      throw error
    }
  }

  /**
   * Picks up the opportunity.
   *
   * @param opportunity
   * @param team
   */
  async pickup(opportunity: Opportunity) {
    // Mark it as being picked up so in case we get the job,
    // we won't trigger the Vanished UI.
    // If it fails, undo it.
    const undo = opportunity.markAsPickingUp()
    try {
      const result = await this.pickupDelegate(opportunity.id)
      if (result !== PickupResult.Success) {
        undo()
      }

      if (this.opportunities.items.includes(opportunity)) {
        // The opportunity is still in the collection, that means
        // we haven't gotten the realtime event that we picked up the job yet.
        // We can optimistically synthesize one and emit it here so the UI
        // updates immediately, and the subsequent event that comes in later
        // will be safely ignored. This ensures that if our realtime connection
        // is down, the UI still updates when we pick up jobs.
        this.opportunities.remove(opportunity)
        this.synthesizedEvents.next({ opportunity, type: 'OpportunityRemoved' })
      }

      return result
    } catch (err) {
      undo()
      throw err
    }
  }

  /**
   * Applies for the opportunity.
   */
  async applyForOpportunity(opportunity: Opportunity) {
    const id = Id.assert(opportunity.id)

    await this.opsAPI.apply(new ApplyToOpportunityRequest().setId(id))

    opportunity.markUserAsCandidate(true)
  }

  /**
   * Withdraws the application from the opportunity.
   */
  async withdrawApplication(opportunity: Opportunity) {
    const id = Id.assert(opportunity.id)

    await this.opsAPI.withdrawApplication(
      new WithdrawApplicationRequest().setId(id),
    )

    opportunity.markUserAsCandidate(false)
  }

  /**
   * Probes whether we can view opportunities.
   */
  probeCanViewOpportunities = once(async () => {
    try {
      await this.opsAPI.listOpportunities(
        new ListOpportunitiesRequest().setPageSize(0),
      )
      return true
    } catch (e) {
      return false
    }
  })

  /**
   * Maps the proto message to an event type.
   *
   * @param e
   * @private
   */
  private async mapOpportunityRealtimeEvent(
    e: RealtimeEvent,
  ): Promise<OpportunityEvent | null> {
    switch (e.getTypeCase()) {
      case TypeCase.OPPORTUNITY_UPDATED: {
        const updatedOp = e.getOpportunityUpdated()
        const opId = updatedOp?.getId()

        const isCandidate = await this.fetchCandidacies(
          opId ? [opId] : [],
        ).then((candidacies) => candidacies.length === 1)

        return runInAction(() => {
          const proto = updatedOp?.toObject()
          // Whether (based on local data) this is considered the "became available" update.
          const becameAvailable = !this.opportunities.get(proto?.id)
          const opportunity = this.opportunities.set(proto)
          opportunity.markUserAsCandidate(isCandidate)

          return {
            type: 'OpportunityUpdated',
            opportunity,
            becameAvailable,
          }
        })
      }
      case TypeCase.OPPORTUNITY_REMOVED: {
        const payload = e.getOpportunityRemoved()!
        const op = this.opportunities.get(payload.getId())
        if (!op) {
          // We either never saw the opportunity or we already synthesized a
          // Removed event.
          return null
        }
        // We got the event in realtime and we haven't removed the Op ourselves,
        // this means that we didn't pick it up.
        this.opportunities.remove(op)
        return { type: 'OpportunityRemoved', opportunity: op }
      }
    }
    return null
  }
}

/**
 * Fields that the Opportunities can be sorted by.
 */
export type OpportunitySortingField =
  | 'created'
  | 'deadline'
  | 'modified'
  | 'transmitted'
  | 'customer_first_name'
  | 'name'
  | 'description'
  | 'score'

/**
 * Query for listing opportunities.
 */
export interface ListOpportunitiesQuery extends CursorPageQuery {
  workspaceId?: string
  searchText?: string
  orderBy?: Partial<Record<OpportunitySortingField, 'asc' | 'desc'>>
}

/**
 * Creates an order_by field.
 *
 * @param orderBy
 */
function createOrderByField(orderBy: ListOpportunitiesQuery['orderBy']) {
  const buf: string[] = []
  if (!orderBy) {
    return ''
  }

  for (const [field, order] of Object.entries(orderBy)) {
    buf.push(`${field} ${order}`)
  }
  return buf.join(',')
}
